import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Card, Col, Empty, Row, Switch, Table, Tooltip, Typography } from "antd";
import { LoadingOverlay } from "components";
import React from "react";

const { Title } = Typography;

const NotificationSettingsTable = ({
    configState = {},
    hasChanges = false,
    onToggleChange = () => {},
    onUpdate = () => {},
    busy = false,
    busyMessage = "",
    notificationPermission,
}) => {
    const isEditable = notificationPermission?.canEdit;
    const columns = React.useMemo(
        () => [
            {
                title: "Notification Type",
                dataIndex: "name",
                key: "name",
                width: 280,
                render: (text, record) => (
                    <div>
                        {record.name}
                        {record.description && (
                            <Tooltip title={record.description}>
                                <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 5, cursor: "help" }} />
                            </Tooltip>
                        )}
                    </div>
                ),
            },
            {
                title: "Info",
                align: "center",
                children: [
                    {
                        title: "Email",
                        key: "info-email",
                        dataIndex: ["settings", "info", "email"],
                        align: "center",
                        width: 90,
                        render: (checked, record) => (
                            <Switch checked={!!checked} onChange={() => onToggleChange(record.eventType, "info", "email")} disabled={!record.settings?.info || !isEditable} />
                        ),
                    },
                    {
                        title: "In-App",
                        key: "info-inapp",
                        dataIndex: ["settings", "info", "inApp"],
                        align: "center",
                        width: 90,
                        render: (checked, record) => (
                            <Switch checked={!!checked} onChange={() => onToggleChange(record.eventType, "info", "inApp")} disabled={!record.settings?.info || !isEditable} />
                        ),
                    },
                ],
            },
            {
                title: "Warning",
                align: "center",
                children: [
                    {
                        title: "Email",
                        key: "warning-email",
                        dataIndex: ["settings", "warning", "email"],
                        align: "center",
                        width: 90,
                        render: (checked, record) => (
                            <Switch checked={!!checked} onChange={() => onToggleChange(record.eventType, "warning", "email")} disabled={!record.settings?.warning || !isEditable} />
                        ),
                    },
                    {
                        title: "In-App",
                        key: "warning-inapp",
                        dataIndex: ["settings", "warning", "inApp"],
                        align: "center",
                        width: 90,
                        render: (checked, record) => (
                            <Switch checked={!!checked} onChange={() => onToggleChange(record.eventType, "warning", "inApp")} disabled={!record.settings?.warning || !isEditable} />
                        ),
                    },
                ],
            },
            {
                title: "Error",
                align: "center",
                children: [
                    {
                        title: "Email",
                        key: "error-email",
                        dataIndex: ["settings", "error", "email"],
                        align: "center",
                        width: 90,
                        render: (checked, record) => (
                            <Switch checked={!!checked} onChange={() => onToggleChange(record.eventType, "error", "email")} disabled={!record.settings?.error || !isEditable} />
                        ),
                    },
                    {
                        title: "In-App",
                        key: "error-inapp",
                        dataIndex: ["settings", "error", "inApp"],
                        align: "center",
                        width: 90,
                        render: (checked, record) => (
                            <Switch checked={!!checked} onChange={() => onToggleChange(record.eventType, "error", "inApp")} disabled={!record.settings?.error || !isEditable} />
                        ),
                    },
                ],
            },
        ],
        [onToggleChange, isEditable],
    );

    const groupedData = React.useMemo(() => {
        const groups = {};
        if (configState && typeof configState === "object") {
            Object.entries(configState).forEach(([eventType, item]) => {
                const category = item.categoryDisplayName || "Uncategorized";
                if (!groups[category]) {
                    groups[category] = [];
                }
                const settings = item.settings && typeof item.settings === "object" ? item.settings : {};

                groups[category].push({
                    key: eventType,
                    eventType,
                    name: item.name,
                    description: item.description,
                    settings: settings,
                });
            });
        }
        const sortedCategories = Object.keys(groups).sort();
        const orderedGroups = {};
        sortedCategories.forEach((key) => {
            orderedGroups[key] = groups[key];
        });
        return orderedGroups;
    }, [configState]);

    const hasAnyData = Object.keys(groupedData).some((category) => groupedData[category].length > 0);
    const isLoadingInitialData = busy && busyMessage && busyMessage.includes("Loading notification settings...");

    return (
        <div style={{ height: "100%", position: "relative" }}>
            <LoadingOverlay busy={busy} spinner message={busyMessage || (isLoadingInitialData && !hasAnyData ? "Loading notification settings..." : "Please wait...")} />
            <Card
                style={{
                    borderRadius: 12,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    visibility: isLoadingInitialData && !hasAnyData ? "hidden" : "visible",
                }}
                styles={{
                    body: {
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        height: "100%",
                    },
                }}
            >
                <div
                    style={{
                        padding: "16px 24px",
                        borderBottom: "1px solid #f0f0f0",
                        background: "#fff",
                        zIndex: 1,
                    }}
                >
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={4} style={{ margin: 0 }}>
                                Manage Notifications
                            </Title>
                        </Col>
                        <Col>
                            <Button type="primary" disabled={!hasChanges || busy || !isEditable} onClick={onUpdate}>
                                Update
                            </Button>
                        </Col>
                    </Row>
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px 24px" }}>
                    {!isLoadingInitialData && !hasAnyData && !busy ? (
                        <Empty description="No notification settings available" style={{ marginTop: "50px", paddingTop: "50px" }} />
                    ) : hasAnyData ? (
                        Object.entries(groupedData).map(([categoryName, items]) => {
                            if (items.length === 0) return null;
                            return (
                                <div key={categoryName} style={{ marginTop: "24px" }}>
                                    <Title level={5} style={{ marginBottom: "16px" }}>
                                        {categoryName}
                                    </Title>
                                    <Table columns={columns} dataSource={items} pagination={false} bordered scroll={{ x: "max-content" }} size="middle" />
                                </div>
                            );
                        })
                    ) : null}
                </div>
            </Card>
        </div>
    );
};

export default NotificationSettingsTable;
